import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer, Field, EmptyState } from "@/components/admin/ui";
import { INVOICE_STATUS_META, formatCents, fmtDate } from "./billingMeta";
import { FileText } from "lucide-react";

export const CustomerDetailDrawer = ({ API, customerId, onClose, onOpenInvoice }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/org/billing/customers/${customerId}`, { withCredentials: true }).then(({ data }) => setData(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, customerId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <DetailDrawer open onClose={onClose} title="Customer" testId="billing-customer-detail-drawer"><p className="text-sm font-semibold text-red-600" data-testid="billing-customer-detail-error">{error}</p></DetailDrawer>;
  if (!data) return <DetailDrawer open onClose={onClose} title="Loading…" testId="billing-customer-detail-drawer"><p className="text-sm text-slate-400" data-testid="billing-customer-detail-loading">Loading…</p></DetailDrawer>;

  const c = data.customer;
  return (
    <DetailDrawer open onClose={onClose} title={`${c.first_name} ${c.last_name}`} subtitle="Billing summary for this customer." testId="billing-customer-detail-drawer" wide>
      <div className="space-y-5" data-testid="billing-customer-detail">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Email" value={c.email} testId="billing-customer-detail-email" />
          <Field label="Phone" value={c.phone} testId="billing-customer-detail-phone" />
          <Field label="Customer Since" value={fmtDate(c.created_at)} testId="billing-customer-detail-since" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10"><p className="text-xs font-bold uppercase text-slate-400">Outstanding</p><p className="mt-1 text-xl font-extrabold" data-testid="billing-customer-detail-outstanding">{formatCents(data.outstanding_cents)}</p></div>
          <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10"><p className="text-xs font-bold uppercase text-slate-400">Total Invoiced</p><p className="mt-1 text-xl font-extrabold" data-testid="billing-customer-detail-total-invoiced">{formatCents(data.total_invoiced_cents)}</p></div>
          <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10"><p className="text-xs font-bold uppercase text-slate-400">Total Paid</p><p className="mt-1 text-xl font-extrabold" data-testid="billing-customer-detail-total-paid">{formatCents(data.total_paid_cents)}</p></div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Invoices</p>
          {data.invoices.length === 0 ? (
            <div className="mt-2"><EmptyState icon={FileText} title="No invoices for this customer yet." testId="billing-customer-detail-invoices-empty" /></div>
          ) : (
            <div className="mt-2 space-y-2" data-testid="billing-customer-detail-invoices">
              {data.invoices.map((inv) => (
                <button key={inv.invoice_id} type="button" onClick={() => onOpenInvoice(inv.invoice_id)} data-testid={`billing-customer-detail-invoice-${inv.invoice_id}`} className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                  <div><p className="text-sm font-bold text-[#0052FF]">{inv.display_id}</p><p className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(inv.due_date)}</p></div>
                  <div className="text-right"><p className="text-sm font-bold">{formatCents(inv.total_cents)}</p><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${INVOICE_STATUS_META[inv.effective_status].cls}`}>{INVOICE_STATUS_META[inv.effective_status].label}</span></div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
};
